// Returning user detection and popup logic

const FIRST_VISIT_KEY = (userId) => `firstVisit_${userId}`;
const UPSELL_POPUP_DISMISSED_KEY = (userId) => `premiumPopupDismissed_${userId}`;
const RENEWAL_POPUP_DISMISSED_KEY = (userId) => `renewalPopupDismissed_${userId}`;

/**
 * Check if this is user's first visit
 */
export function isFirstVisit(userId) {
  const key = FIRST_VISIT_KEY(userId);
  const visited = localStorage.getItem(key);

  if (!visited) {
    localStorage.setItem(key, Date.now().toString());
    return true;
  }

  return false;
}

/**
 * Check if should show premium upsell popup
 * Show on 2nd+ visit if:
 * - User is NOT premium
 * - Popup wasn't dismissed in last 7 days
 */
export function shouldShowUpsellPopup(userId, isPremium) {
  if (isPremium) return false;

  const isFirst = isFirstVisit(userId);
  if (isFirst) return false; // Don't show on first visit

  const dismissedKey = UPSELL_POPUP_DISMISSED_KEY(userId);
  const lastDismissed = localStorage.getItem(dismissedKey);

  if (!lastDismissed) return true; // Never dismissed

  const daysSinceDismissed = (Date.now() - parseInt(lastDismissed)) / (1000 * 60 * 60 * 24);
  return daysSinceDismissed >= 7; // Show again after 7 days
}

/**
 * Dismiss upsell popup for 7 days
 */
export function dismissUpsellPopup(userId) {
  const dismissedKey = UPSELL_POPUP_DISMISSED_KEY(userId);
  localStorage.setItem(dismissedKey, Date.now().toString());
}

/**
 * Check if should show renewal reminder popup
 * Show if:
 * - User IS premium
 * - Subscription expires in 10 days or less
 * - Popup wasn't dismissed in last 3 days
 */
export function shouldShowRenewalPopup(subscriptionEndDate, userId) {
  if (!subscriptionEndDate) return false;

  const now = new Date();

  // Handle both Firestore Timestamp and Date objects
  let expiryDate;
  if (subscriptionEndDate.toDate) {
    // Firestore Timestamp
    expiryDate = subscriptionEndDate.toDate();
  } else if (subscriptionEndDate instanceof Date) {
    expiryDate = subscriptionEndDate;
  } else {
    expiryDate = new Date(subscriptionEndDate);
  }

  const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

  // Only show if expiring in exactly 10 days or less (not more)
  if (daysRemaining > 10 || daysRemaining <= 0) return false; // Not in range

  const dismissedKey = RENEWAL_POPUP_DISMISSED_KEY(userId);
  const lastDismissed = localStorage.getItem(dismissedKey);

  if (!lastDismissed) return true; // Never dismissed

  const daysSinceDismissed = (Date.now() - parseInt(lastDismissed)) / (1000 * 60 * 60 * 24);
  return daysSinceDismissed >= 3; // Show again after 3 days
}

/**
 * Dismiss renewal popup for 3 days
 */
export function dismissRenewalPopup(userId) {
  const dismissedKey = RENEWAL_POPUP_DISMISSED_KEY(userId);
  localStorage.setItem(dismissedKey, Date.now().toString());
}

/**
 * Clear all popup dismissals (for testing)
 */
export function clearPopupDismissals(userId) {
  localStorage.removeItem(UPSELL_POPUP_DISMISSED_KEY(userId));
  localStorage.removeItem(RENEWAL_POPUP_DISMISSED_KEY(userId));
}

/**
 * Clear all user detection data (for testing)
 */
export function clearAllUserData(userId) {
  localStorage.removeItem(FIRST_VISIT_KEY(userId));
  localStorage.removeItem(UPSELL_POPUP_DISMISSED_KEY(userId));
  localStorage.removeItem(RENEWAL_POPUP_DISMISSED_KEY(userId));
}
